import { useState, useCallback, useRef } from 'react'
import { z } from 'zod'
import cl from 'clsx'

import { NewIcon } from '@/shared/assets'
import { useOptimisticValue } from '@/shared/hooks'
import { AnalyticsTableRowTypeChannels } from '@/features/analytics/types'
import { NiceChannelBox } from '@/features/analytics/components/nice-channel-box'
import { parseTelegramPeerReference } from '@synapex/shared2/telegram'
import { useOnClickOutside } from 'usehooks-ts'

type NameCellData = Pick<
    AnalyticsTableRowTypeChannels,
    | 'link_id'
    | 'name'
    | 'rknStatus'
    | 'title'
    | 'link'
    | 'photoUrl'
    | 'creationDate'
>

const nameSchema = z.string().max(50, 'Name must be at most 50 characters long')

const isLinkNew = (data: NameCellData) =>
    Date.now() - new Date(data.creationDate).getTime() <= 10 * 1000

export type NameCellProps = {
    data: NameCellData

    onChange: (v: string) => void

    //toggle mode
    edit: boolean
    onChangeEdit: (v: boolean) => void
}
export const NameCell = ({
    data,

    onChange,

    edit,
    onChangeEdit,
}: NameCellProps) => {
    const { value: name, setOptimisticValue } = useOptimisticValue(data.name)

    const enableEdit = useCallback(() => onChangeEdit(true), [onChangeEdit])

    const onChangeValue = useCallback(
        (value: string) => {
            const result = nameSchema.safeParse(value)

            if (result.success) {
                setOptimisticValue(value)

                onChange(value)
                onChangeEdit(false)
            }
        },
        [onChangeEdit, setOptimisticValue, onChange]
    )

    const linkIsNew = isLinkNew(data)

    const channelRef = parseTelegramPeerReference(name)

    return edit ? (
        <NameInput value={name} onChangeValue={onChangeValue} />
    ) : (
        <div className={'flex items-center text-left'} onClick={enableEdit}>
            {linkIsNew && (
                <img src={NewIcon} alt="New" className="name-cell__new-icon" />
            )}
            {channelRef ? (
                <NiceChannelBox
                    {...channelRef}
                    title={data.title}
                    className={cl('name-cell__channel-box', {
                        new: linkIsNew,
                    })}
                    photoUrl={data.photoUrl}
                    rknStatus={!data.link ? 'loading' : data.rknStatus}
                />
            ) : (
                <div className="pl-2">{name}</div>
            )}
        </div>
    )
}

type NameInputProps = {
    value: string
    onChangeValue: (value: string) => void
    onValidationError?: (err: z.ZodError) => void
}
const NameInput = ({
    value,
    onChangeValue,
    onValidationError,
}: NameInputProps) => {
    const inputRef = useRef(null)
    const [internalValue, setInternalValue] = useState(value)

    const onChange: React.ChangeEventHandler<HTMLInputElement> = (evt) => {
        const value = evt.target.value
        setInternalValue(evt.target.value)

        if (onValidationError) {
            const result = nameSchema.safeParse(value)
            if (!result.success) {
                onValidationError(result.error)
            }
        }
    }

    const onEnter = (evt: React.KeyboardEvent<HTMLInputElement>) => {
        if (evt.key === 'Enter') {
            onChangeValue(internalValue)
        }
    }

    const onBlur = () => {
        onChangeValue(internalValue)
    }

    useOnClickOutside(inputRef, onBlur)

    return (
        <input
            ref={inputRef}
            type="text"
            value={internalValue}
            onChange={onChange}
            onBlur={onBlur}
            onKeyDown={onEnter}
            className={cl('edit-input', 'text-left')}
        />
    )
}
